import { Navigate } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'

/**
 * Port of the legacy `$rootScope.$on('$stateChangeStart', ...)` guard
 * registered in `app/main.js` (see the `app.run(...)` block at the bottom
 * of that file):
 *
 *   $rootScope.$on('$stateChangeStart', function(event, toState, toParams) {
 *     var requireLogin = toState.data.requireLogin;
 *     if (requireLogin) {
 *       if (AuthService.isAuthenticated()) {
 *         $rootScope.loggeduser = AuthService.loggeduser;
 *       } else {
 *         event.preventDefault();
 *         $state.go('login');
 *       }
 *     }
 *   });
 *
 * Every one of the 23 legacy states declares `data: { requireLogin: ... }`
 * (see `app/main.js`); only the `login` state (url `/`) has
 * `requireLogin: false` — all other 22 states (the `workspace` shell and
 * every `workspace.*` child) have `requireLogin: true`. There is no
 * per-route variation beyond that boolean, so a single wrapper applied to
 * the whole `/workspace` subtree (its single layout route in
 * `AppRoutes.jsx`) reproduces the same behavior as guarding every child
 * state individually, without repeating the check 22 times.
 *
 * Behavior ported 1:1:
 * - Unauthenticated access to a `requireLogin: true` route redirects to the
 *   `login` state (`$state.go('login')` -> `<Navigate to="/login" />`).
 * - `replace` is used so the blocked attempt doesn't linger in browser
 *   history (closest equivalent to `event.preventDefault()` stopping the
 *   transition rather than completing it).
 *
 * Behavior intentionally NOT ported (does not exist in the legacy app,
 * confirmed by reading `app/register/loginController.js` and
 * `app/main.js` — `$state.go('workspace')` only ever fires from the login
 * form's success callback, never from a guard/redirect elsewhere):
 * - The legacy guard has no "already authenticated user hitting `/login`
 *   gets redirected to `/workspace`" rule. That is a *new* behavior some
 *   SPAs add for UX; since it's not present in `app/main.js` or
 *   `loginController.js`, it is a product decision, not something to
 *   silently invent here. Documented in
 *   `specs/react-migration/011-protected-routes-guard.md` execution notes
 *   instead of guessed at in code.
 * - The legacy guard does not preserve the originally requested URL for a
 *   post-login redirect (`$state.go('login')` takes no params tying it back
 *   to `toState`). This wrapper mirrors that 1:1: it does not carry the
 *   attempted location forward for an automatic post-login redirect. See
 *   the same execution notes for the reasoning.
 */
// eslint-disable-next-line react/prop-types
function ProtectedRoute({ children }) {
  const { isAuthenticated } = useAuth()

  if (!isAuthenticated()) {
    return <Navigate to="/login" replace />
  }

  return children
}

export default ProtectedRoute
